import { z } from 'zod';

/**
 * This is what determines our language support in the GTV ecosystem.
 */
export const galateaTVSupportedLanguageCodes = [
  'en',
  'de',
  'fr',
  'es',
  'es-419', // Spanish (Latin America)
  'it',
  'pt-BR',
  'tr',
  'pl',
  'ru',
  'nl',
] as const;

export type LanguageCode = (typeof galateaTVSupportedLanguageCodes)[number];

const DEFAULT_LANGUAGE = 'en';

/**
 * We want Spanish from the Americas to be different than Spanish from Spain.
 */
export const galateaTVSpecialLanguageMappings: Record<string, LanguageCode> = {
  'es-MX': 'es-419', // (Spanish - Mexico)
  'es-US': 'es-419', // (Spanish - United States)
  'es-AR': 'es-419', // (Spanish - Argentina)
  'es-BO': 'es-419', // (Spanish - Bolivia)
  'es-CL': 'es-419', // (Spanish - Chile)
  'es-CO': 'es-419', // (Spanish - Colombia)
  'es-CR': 'es-419', // (Spanish - Costa Rica)
  'es-DO': 'es-419', // (Spanish - Dominican Republic)
  'es-EC': 'es-419', // (Spanish - Ecuador)
  'es-SV': 'es-419', // (Spanish - El Salvador)
  'es-GT': 'es-419', // (Spanish - Guatemala)
  'es-HN': 'es-419', // (Spanish - Honduras)
  'es-NI': 'es-419', // (Spanish - Nicaragua)
  'es-PA': 'es-419', // (Spanish - Panama)
  'es-PE': 'es-419', // (Spanish - Peru)
  'es-PR': 'es-419', // (Spanish - Puerto Rico)
  'es-UY': 'es-419', // (Spanish - Uruguay)
  'es-VE': 'es-419', // (Spanish - Venezuela)
};

export const languageCodesSchema = z.enum(galateaTVSupportedLanguageCodes).catch(DEFAULT_LANGUAGE);

export type LanguageMap = Partial<{
  [key in LanguageCode]: string;
}>;

export const isLanguageCode = (value: string): value is LanguageCode =>
  galateaTVSupportedLanguageCodes.includes(value as LanguageCode);

/**
 * Collapses Titan's API language code to the shorter form web/mobile expect.
 * Two regional codes collapse to their parent:
 *  - `pt-BR` → `pt`    (web/i18next only ship `pt` resources)
 *  - `es-419` → `es`   (Latam Spanish has no dedicated web locale or bundle)
 *
 * All other codes pass through unchanged. Used for candyjar URL locale
 * prefixes, i18next lookups, and similar places where the consumer's locale
 * set doesn't carry the country/region suffix.
 */
export const sanitizeLanguageCode = (languageCode: string): string => {
  if (languageCode === 'pt-BR') {
    return 'pt';
  }
  if (languageCode === 'es-419') {
    return 'es';
  }
  return languageCode;
};

/**
 * Subtitle-track fallbacks: when an episode has no subtitle row for the
 * requested language, serve the mapped language's track instead of none.
 * Latam Spanish falls back to Spain Spanish — a slightly-off dialect beats
 * missing subtitles entirely.
 */
export const subtitleFallbackLanguages: Partial<Record<LanguageCode, LanguageCode>> = {
  'es-419': 'es',
};

export const getSubtitleFallbackLanguage = (language: LanguageCode): LanguageCode | undefined =>
  subtitleFallbackLanguages[language];

/**
 * Used on Calypso.
 */
export const internalLanguageLabels: Record<LanguageCode, string> = {
  en: 'English',
  de: 'German',
  fr: 'French',
  es: 'Spanish (Spain)',
  'es-419': 'Spanish (Latam)',
  'pt-BR': 'Portuguese',
  it: 'Italian',
  tr: 'Turkish',
  pl: 'Polish',
  nl: 'Dutch',
  ru: 'Russian',
};

export const internalEmojiLanguageLabels: Record<LanguageCode, string> = {
  en: '🇺🇸',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  'pt-BR': '🇧🇷',
  'es-419': '🇲🇽',
  it: '🇮🇹',
  tr: '🇹🇷',
  pl: '🇵🇱',
  nl: '🇳🇱',
  ru: '🇷🇺',
};

/**
 * Used on GTV Client
 */
export const clientFacingLanguageLabels: Record<LanguageCode, string> = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  'pt-BR': 'Portuguese (Brazil)',
  'es-419': 'Español (Latinoamérica)',
  it: 'Italiano',
  tr: 'Türkçe',
  pl: 'Polski',
  ru: 'Pусский',
  nl: 'Nederlands',
};
